import { message, Modal, Select, Spin } from 'antd';
import React from 'react';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import useKanmonProducts from 'hooks/useKanmonProducts';
import { TKanmonPricePlan, TKanmonProduct } from 'types/financials';
import { selectKanmonProduct } from 'api/financials';

type Props = {
    open: boolean,
    onCancel: () => void,
    onSuccess: () => void,
};

const PaybackTermModal = (props: Props) => {
    const { open, onCancel, onSuccess } = props;
    const [products, loadingProducts, refetchProducts] = useKanmonProducts();
    const [targetProduct, setTargetProduct] = React.useState<TKanmonProduct | null>(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [changePlan, setChangePlan] = React.useState<TKanmonPricePlan | null>(null);

    const handleOk = async () => {
        setSubmitting(true);
        const result = await selectKanmonProduct(targetProduct?.productId!, changePlan?.pricePlanId!);
        if (!result.error) {
            message.success('Update payback term successfully');
            onSuccess();
        }
        setSubmitting(false);
    };

    React.useEffect(() => {
        if (open) {
            setTargetProduct(null);
            refetchProducts();
        }
    }, [open]);

    React.useEffect(() => {
        if (products.length > 0) {
            let target: TKanmonProduct | undefined = products[0];
            if (products.length > 1) {
                target = products.find((item) => item.productStatus === 'CURRENT');
                if (!target) {
                    target = products[0];
                }
            }
            setTargetProduct(target);
            setChangePlan(target.pricePlans.find((item) => item.selected) || null);
        }
    }, [products]);

    return (
        <div
            className={s.modal}
        >
            <Modal
                open={open}
                onCancel={onCancel}
                okText="Save"
                title="Set your Klarity Pay Later invoice payback term"
                className={commonS.modalFixHeightWrap}
                onOk={handleOk}
                width={540}
                okButtonProps={{
                    loading: submitting,
                }}
            >
                <div className={s.wrap}>
                    <div className={s.title}>Select the default term length for your Klarity Pay Later invoice payback. You can change this anytime in Settings.
                    </div>
                    <Spin spinning={loadingProducts}>
                        <div className={s.option}>
                            {
                                targetProduct?.pricePlans.map((item) => {
                                    const isSelected = changePlan?.pricePlanId === item.pricePlanId;
                                    return (
                                        <div
                                            onClick={() => {
                                                setChangePlan(item);
                                            }}
                                            key={item.pricePlanId}
                                            className={`${s.item} ${isSelected ? s.itemSelected : ''}`}
                                        >
                                            <div className={s.itemTitle}>{item.repaymentDurationDays} days</div>
                                            <div className={s.itemDes}>Transaction fee {item.transactionFeePercentage} % based on your offer.</div>
                                            {
                                                isSelected && (
                                                    <span className={s.itemSelectIcon}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                            <path fillRule="evenodd" clipRule="evenodd" d="M9.6 19.2C14.9019 19.2 19.2 14.9019 19.2 9.6C19.2 4.29807 14.9019 0 9.6 0C4.29807 0 0 4.29807 0 9.6C0 14.9019 4.29807 19.2 9.6 19.2ZM14.0485 8.04853C14.5172 7.5799 14.5172 6.8201 14.0485 6.35147C13.5799 5.88284 12.8201 5.88284 12.3515 6.35147L8.4 10.3029L6.84853 8.75147C6.3799 8.28284 5.6201 8.28284 5.15147 8.75147C4.68284 9.2201 4.68284 9.9799 5.15147 10.4485L7.55147 12.8485C8.0201 13.3172 8.7799 13.3172 9.24852 12.8485L14.0485 8.04853Z" fill="#0E62F4" />
                                                        </svg>
                                                    </span>
                                                )
                                            }
                                        </div>
                                    );
                                })
                            }
                        </div>
                    </Spin>
                </div>
            </Modal>
        </div>
    );
};

export default PaybackTermModal;
